<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        class="staff-form"
        @closed="$emit('close')"
    >
        <template #header>
            <div class="dialog__header">
                <div class="dialog__header--title">
                    <h2 class="my-1">
                        {{
                            isEdition
                                ? $l10n.__('Modifier le collaborateur')
                                : $l10n.__('Ajouter un collaborateur')
                        }}
                    </h2>
                    <p class="m-0 gray-60 fs-3">
                        {{
                            isEdition
                                ? $l10n.__('Modifier un membre de votre équipe !')
                                : $l10n.__('Ajouter un nouveau membre à votre équipe !')
                        }}
                    </p>
                </div>
                <div class="dialog__header--close">
                    <CompassIconButton icon="close" @click="$emit('close')" />
                </div>
            </div>
        </template>
        <template #default>
            <div class="dialog__default">
                <div class="m-auto">
                    <el-form
                        ref="staffForm"
                        :model="form"
                        :rules="rules"
                        labelWidth="150px"
                        class="el-form-LH32 flex-row mb-3"
                        labelPosition="top"
                    >
                        <el-form-item prop="civility" :error="errors.get('civility')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Civilité') }}
                                </span>
                            </template>
                            <el-radio-group
                                v-model="form.civility"
                                mode="edit"
                                :options="civilities"
                                class="wp-4"
                            >
                                <el-radio
                                    v-for="civility in civilities"
                                    :key="civility.label"
                                    :label="civility.value"
                                >
                                    {{ civility.label }}
                                </el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item prop="firstname" :error="errors.get('firstname')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Prénom') }}
                                </span>
                            </template>
                            <el-input v-model="form.firstname"></el-input>
                        </el-form-item>
                        <el-form-item prop="lastname" :error="errors.get('lastname')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Nom') }}
                                </span>
                            </template>
                            <el-input v-model="form.lastname"></el-input>
                        </el-form-item>
                        <el-form-item prop="email" :error="errors.get('email')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Email') }}
                                </span>
                            </template>
                            <el-input v-model="form.email" type="email"></el-input>
                        </el-form-item>
                        <el-form-item prop="mobile_number" :error="errors.get('mobile_number')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Portable') }}
                                </span>
                            </template>
                            <CompassTelInput
                                v-model="form.mobile_number"
                                defaultCountry="fr"
                                menuClass="staff-form__tel-input-dropdown"
                                class="staff-form__tel-input"
                            />
                        </el-form-item>
                        <el-form-item prop="phone_number" :error="errors.get('phone_number')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Ligne fixe') }}
                                </span>
                            </template>

                            <CompassTelInput
                                v-model="form.phone_number"
                                defaultCountry="fr"
                                menuClass="staff-form__tel-input-dropdown"
                                class="staff-form__tel-input"
                            />
                        </el-form-item>

                        <div v-if="!isSellsyCorp">
                            <el-form-item prop="team" :error="errors.get('team')">
                                <template #label>
                                    <span class="gray-100">
                                        {{ $l10n.__('Equipe') }}
                                    </span>
                                </template>
                                <select-input
                                    v-model="form.team"
                                    mode="edit"
                                    :options="teams"
                                    :placeholder="$t.__('common.actions.select')"
                                    labelColumn="name"
                                    idColumn="id"
                                    :appendToBody="true"
                                    @input="resetJob"
                                />
                            </el-form-item>
                            <el-form-item prop="job" :error="errors.get('job')">
                                <template #label>
                                    <span class="gray-100">
                                        {{ $l10n.__('Poste') }}
                                    </span>
                                </template>
                                <select-input
                                    v-model="form.job"
                                    mode="edit"
                                    :options="jobs"
                                    :placeholder="$t.__('common.actions.select')"
                                    labelColumn="name"
                                    idColumn="id"
                                    :appendToBody="true"
                                />
                            </el-form-item>
                        </div>

                        <el-form-item
                            class="flex-column separator"
                            prop="profile"
                            :error="errors.get('profile')"
                        >
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Profil de privilèges') }}
                                </span>
                            </template>
                            <select-input
                                v-model="form.profile"
                                mode="edit"
                                :options="profiles"
                                :placeholder="$t.__('forms.StaffForm.select_profil')"
                                labelColumn="name"
                                idColumn="id"
                                :appendToBody="true"
                            />
                            <span class="form-item-caption typographies-paragraph-small-regular">
                                {{
                                    $l10n.__(
                                        'Si vous voulez accorder à votre collaborateur un accès à l’ensemble des fonctionnalités et paramétrages du compte, sélectionnez le profil Administrateur',
                                    )
                                }}
                            </span>
                        </el-form-item>
                        <el-form-item>
                            <p class="bold pb-2">{{ $l10n.__('Paramétrage Sellsy') }}</p>
                            <p class="grey pb-3">
                                {{
                                    $l10n.__(
                                        'Veuillez choisir le profil de privilèges à associer à ce collaborateur. Vous pouvez créer / modifier des profils de privilèges via la page de gestion des profils utilisateurs',
                                    )
                                }}
                            </p>
                            <el-checkbox
                                v-if="!isEdition"
                                v-model="form.first_connection_onboarding"
                            >
                                {{
                                    $l10n.__(
                                        'Autoriser le collaborateur a modifier ses informations',
                                    )
                                }}
                            </el-checkbox>
                        </el-form-item>
                        <el-form-item
                            v-if="hasLicenseManagement"
                            prop="licenses"
                            :label="$l10n.__('Licence')"
                            :error="errors.get('licenses')"
                        >
                            <div class="flex-row licenses mb-2">
                                <a
                                    href="https://go.sellsy.com/offres"
                                    target="_blank"
                                    class="ml-3 fs-2 semibold link"
                                >
                                    {{ $l10n.__('Comparer les licences') }}
                                </a>
                            </div>
                            <select-input
                                v-model="form.licenses[0].id"
                                mode="edit"
                                :options="availableLicenses"
                                :placeholder="$t.__('forms.StaffForm.select_licence')"
                                labelColumn="label"
                                idColumn="value"
                                :appendToBody="true"
                            />
                        </el-form-item>
                        <el-form-item prop="timezone" :error="errors.get('timezone')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Fuseau horaire') }}
                                </span>
                            </template>
                            <select-input
                                v-model="form.timezone"
                                mode="edit"
                                :filterable="true"
                                idColumn="value"
                                labelColumn="name"
                                :options="timezones"
                                :appendToBody="true"
                            />
                        </el-form-item>
                        <el-form-item prop="language" :error="errors.get('language')">
                            <template #label>
                                <span class="gray-100">
                                    {{ $l10n.__('Langue') }}
                                </span>
                            </template>
                            <select-input
                                v-model="form.language"
                                mode="edit"
                                :options="languages"
                                :appendToBody="true"
                            />
                        </el-form-item>
                    </el-form>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="$emit('close')"
                />
                <CompassButton
                    :label="primaryButtonText"
                    type="primary"
                    class="ml-3"
                    :loading="isFormSubmitting"
                    @click="validateAndSubmit('staffForm')"
                />
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import SelectInput from 'vcomponents/inputs/SelectInput.vue';
import FormMixin from 'mixins/FormMixins';
import StaffRules from 'vcomponents/forms/rules/StaffRules';

import Civilities from 'Config/authenticated/Civilities';
import Timezones from 'Config/authenticated/Timezones';
import { staffLanguages } from 'Config/authenticated/Languages';

import StaffApi from 'api/staff/staff';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateCorp } = createNamespacedHelpers('corp');
const { mapState, mapActions } = createNamespacedHelpers('subscriptionManager');

const staffSchema = {
    civility: 'mr',
    firstname: '',
    lastname: '',
    email: '',
    mobile_number: '',
    phone_number: '',
    fax_number: '',
    team: null,
    job: null,
    first_connection_onboarding: false,
    profile: null,
    licenses: [{ id: null }],
    timezone: 'Europe/Paris',
    language: 'fr',
};

export default {
    components: {
        SelectInput,
    },
    mixins: [FormMixin],
    props: {
        staffid: {
            type: [Number, Boolean],
            required: false,
            default: false,
        },
        fromLegacy: {
            type: Boolean,
            required: false,
            default: false,
        },
        isSellsyCorp: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            showModal: true,
            loading: true,
            currentStaff: { ...staffSchema },
            form: { ...staffSchema },
            rules: StaffRules,
            civilities: Civilities,
            timezones: Timezones,
            languages: staffLanguages,
            teams: [],
            profiles: [],
            isFormSubmitting: false,
        };
    },
    computed: {
        ...mapState(['availableLicenses']),
        ...mapStateCorp(['uiLang', 'hasLicenseManagement']),
        jobs() {
            if (this.form.team) {
                return this.teams.find((team) => team.id == this.form.team)?.jobs;
            }
            return [];
        },
        primaryButtonText() {
            return this.isEdition
                ? this.$l10n.__('Modifier le collaborateur')
                : this.$l10n.__('Ajouter ce collaborateur');
        },
        isEdition() {
            return !!this.staffid;
        },
    },
    async created() {
        if (!this.isEdition) {
            const fakeDefaultProfile = {
                id: 0,
                name: this.$l10n.__('Collaborateur'),
            };
            this.profiles = [fakeDefaultProfile];
            this.form.profile = fakeDefaultProfile.id;
        }

        Promise.all([StaffApi.getTeamsList(), StaffApi.getProfilesList('name')])
            .then(([teams, profiles]) => {
                this.teams = teams.data.data;
                this.profiles = profiles.data.data;
                if (this.isEdition) {
                    StaffApi.getStaff(this.staffid)
                        .then((response) => {
                            this.hydrateStaff(response);
                            this.loading = false;
                        })
                        .catch(() => {
                            this.$emit('close');
                            this.$pushToast({
                                type: 'error',
                                icon: true,
                                text: this.$l10n.__(
                                    'Erreur lors de la récupération des informations du collaborateur.',
                                ),
                            });
                        });
                } else {
                    this.loading = false;
                }

                this.initPrivilegeProfileInput();
            })
            .catch(() => {
                this.$emit('close');
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$l10n.__(
                        'Erreur lors de la récupération des informations du collaborateur.',
                    ),
                });
            });

        if (this.hasLicenseManagement) {
            await this.getSubscription();
        }
        this.form.language = this.uiLang;
    },
    methods: {
        ...mapActions(['createStaff', 'getSubscription']),
        resetJob() {
            this.form.job = null;
        },

        validateAndSubmit() {
            this.$refs['staffForm'].validate((valid, fields) => {
                if (valid && !this.errors.hasOne()) {
                    this.save();
                } else {
                    //If no validations errors on frontside, launch the save for validations on backside
                    if (Object.keys(fields).length === 0) {
                        this.save();
                        return;
                    }

                    if (fields['email'] && this.currentStaff.email === this.form.email) {
                        delete fields['email'];
                        if (Object.entries(fields).length === 0) {
                            this.save();
                            return;
                        }
                    }
                    const msg = Object.entries(fields)[0];
                    const match = msg[0].match(
                        /^(groupedCustomFields\.[0-9]{1,}\.fields\.[0-9]{1,}\.value)$/,
                    );

                    if (!_.isNil(msg) && !_.isNil(match)) {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: msg[1][0].message,
                        });
                    } else {
                        const message =
                            msg[1][0].message.length > 0
                                ? msg[1][0].message
                                : this.$l10n.__("Une erreur s'est produite.");
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: message,
                        });
                    }
                    if (!!scroll) {
                        const currentRef = msg[0];
                        let type = 'main';
                        if (!_.isNil(match)) {
                            type = 'custom-fields';
                        } else if (currentRef.indexOf('-') >= 0) {
                            type = currentRef.slice(0, currentRef.indexOf('-'));
                        }

                        //scroll to field if it was referenced
                        if (!_.isNil(match)) {
                            this.$parent?.$refs[type]?.$refs[currentRef][0]?.$el.scrollIntoView({
                                block: 'center',
                                behavior: 'smooth',
                                inline: 'end',
                            });
                        } else {
                            this.$parent?.$refs[type]?.$refs[currentRef]?.$el.scrollIntoView({
                                block: 'center',
                                behavior: 'smooth',
                                inline: 'nearest',
                            });
                        }
                    }
                }
            });
        },

        hydrateStaff(response) {
            this.form = { ...this.form, ...response.staff.data };
            this.form.profile = parseInt(response.staff.data.profile);
            this.form.team = response.staff.data.team
                ? parseInt(response.staff.data.team.id)
                : null;
            this.form.job = response.staff.data.job ? parseInt(response.staff.data.job.id) : null;
            this.form.licenses[0].id = response.staff.data._embed.licenses
                ? response.staff.data._embed.licenses[0].id
                : 'null';
            delete this.form._embed;
            this.currentStaff = { ...this.form };
            Vue.delete(this.form, 'created');
        },
        // eslint-disable-next-line vue/no-unused-properties
        save() {
            this.loading = true;
            this.errors.clearAll();

            if (this.isEdition) {
                this.update();
            } else {
                this.create();
            }
        },
        create() {
            this.isFormSubmitting = true;
            const form = { ...this.form };
            if (!this.hasLicenseManagement) {
                delete form.licenses;
            }
            if (this.hasLicenseManagement && form.licenses[0].id === 'null') {
                form.licenses = [];
            }
            StaffApi.createStaff(form)
                .then((response) => {
                    this.createStaff({ data: response.staff.data, licenses: form.licenses });
                    if (this.fromLegacy) {
                        Utils.reload();
                    }
                    this.$emit('close');
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.details);
                    this.loading = false;
                })
                .finally(() => (this.isFormSubmitting = false));
        },

        update() {
            this.isFormSubmitting = true;
            const form = { ...this.form };
            if (!this.hasLicenseManagement) {
                delete form.licenses;
            }
            if (this.hasLicenseManagement && form.licenses[0].id === 'null') {
                form.licenses = [];
            }
            StaffApi.updateStaff(form)
                .then(() => {
                    this.$emit('close');
                    Utils.reload();
                })
                .catch((error) => {
                    this.errors.setErrors(error.response.data.error.details);
                    this.loading = false;
                })
                .finally(() => (this.isFormSubmitting = false));
        },
        initPrivilegeProfileInput() {
            if (!this.isEdition && this.profiles.some((profile) => !profile.is_administrator)) {
                this.form.profile = this.profiles.filter(
                    (profile) => !profile.is_administrator,
                )[0].id;
            }
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.staff-form {
    .dialog {
        &__header {
            display: flex;
            justify-content: space-between;
            padding: var(--spacing-4) var(--spacing-5);

            &--title {
                display: flex;
                flex-direction: column;
            }

            &--close {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
            }
        }

        &__default {
            padding-bottom: var(--spacing-4);
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 var(--spacing-5) var(--spacing-4);
        }
    }

    .form-item-caption {
        color: var(--colors-common-text-standard-caption);
        margin-top: var(--spacing-0);
        display: inline-block;
    }
}

.el-form-LH32 {
    flex-wrap: wrap;
    .el-form-item__label {
        line-height: var(--spacing-5) !important;
    }
    .el-form-item {
        width: 100%;
        text-align: left;
        overflow: inherit;
        &:nth-child(2),
        &:nth-child(5),
        &:nth-child(12) {
            padding-right: var(--spacing-1);
        }
        &:nth-child(3),
        &:nth-child(6),
        &:nth-child(13) {
            padding-left: var(--spacing-1);
        }
        &:nth-child(2),
        &:nth-child(3),
        &:nth-child(5),
        &:nth-child(6),
        &:nth-child(12),
        &:nth-child(13) {
            width: 50%;
        }

        & > label {
            line-height: 100% !important;
            text-align: left;
            margin-bottom: var(--spacing-1) !important;
            font-weight: $font-weight-semibold;
        }
        .el-form-item__content {
            margin-left: 0 !important;
        }
        .el-radio-group {
            display: inline;
        }

        .staff-form__tel-input > .compass-input > input {
            width: 100%;
        }

        &.separator:after {
            content: '';
            border-top: solid 1px $gray-40;
            margin-top: var(--spacing-4);
        }
        .licenses {
            justify-content: space-between;
        }
    }
}

#compass-dropdown-container {
    .staff-form__tel-input-dropdown {
        z-index: 1001;
    }
}
</style>
